import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getProjects, getCachedProjects } from '../services/projects';
import ProjectImage from '../components/ProjectImage';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Gallery = () => {
  const { t } = useTranslation();
  useScrollAnimation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [projects, setProjects] = useState(() => getCachedProjects() || []);
  const [loading, setLoading] = useState(() => getCachedProjects() === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Categorías de trabajos
  const categories = [
    { key: 'all', label: 'Todos' },
    { key: 'Stands', label: 'Stands' },
    { key: 'Cartelería', label: 'Cartelería' },
    { key: 'Exhibidores', label: 'Exhibidores' },
    { key: 'Letras Corpóreas', label: 'Letras Corpóreas' },
    { key: 'Mobiliario', label: 'Mobiliario' },
    { key: 'Pantallas', label: 'Pantallas' },
    { key: 'Impresión', label: 'Impresión' },
    { key: 'Vinilos', label: 'Vinilos' },
    { key: 'Señalética', label: 'Señalética' },
    { key: 'Otros', label: 'Otros' }
  ];

  // Keep real results on return visits while refreshing them from the database.
  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    setRefreshing(true);
    setLoading(getCachedProjects() === null);

    getProjects(controller.signal)
      .then(data => {
        if (!controller.signal.aborted) setProjects(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    return () => controller.abort();
  }, [attempt]);

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.categoria === selectedCategory);

  const handleProjectClick = (project) => {
    navigate(`/trabajo/${project._id}`);
  };

  return (
    <div id="gallery">
      {/* Hero Section */}
      <section className="gallery-hero py-5 text-white spotlight-effect" style={{
        background: 'linear-gradient(135deg, rgba(30, 80, 150, 0.9) 0%, rgba(22, 61, 115, 0.9) 100%), url("/stand-spacegym.png") center/cover no-repeat'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center animate-on-scroll">
              <h1 className="display-4 fw-bold mb-4">{t('works.title')}</h1>
              <p className="fs-5">{t('works.subtitle')}</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filter Section */}
      <section className="py-2" style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
          <div 
            className="d-flex gap-2"
            style={{ 
              overflowX: 'auto', 
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              padding: '20px',
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setSelectedCategory(cat.key)}
                style={{ 
                  padding: '6px 16px',
                  borderRadius: '20px',
                  border: selectedCategory === cat.key ? 'none' : '1px solid #1E5096',
                  backgroundColor: selectedCategory === cat.key ? '#1E5096' : 'transparent',
                  color: selectedCategory === cat.key ? '#ffffff' : '#1E5096',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-5">
        <Container>
          {error && (
            <Alert variant="warning" role="alert">
              <p className="mb-2">{projects.length > 0
                ? 'No pudimos actualizar los proyectos. Mostramos los últimos proyectos cargados.'
                : 'No pudimos cargar los proyectos. Por favor, intentá nuevamente.'}</p>
              <Button variant="outline-primary" onClick={() => setAttempt(value => value + 1)} disabled={refreshing}>
                {refreshing ? 'Reintentando...' : 'Reintentar'}
              </Button>
            </Alert>
          )}
          {refreshing && !loading && <p className="text-muted" role="status">Actualizando proyectos...</p>}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Cargando trabajos...</p>
            </div>
          ) : (
            <Row>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project, index) => (
                  <Col key={project._id} md={4} className="mb-4">
                    <Card 
                      className="h-100 shadow-sm" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleProjectClick(project)}
                    >
                      <ProjectImage key={project.imagen || project.imagenes?.[0] || project._id} project={project} eager={index < 3} />
                      <Card.Body>
                        <Card.Title style={{ color: '#1E5096' }}>{project.nombre}</Card.Title>
                        <Card.Text className="text-muted">{project.descripcion}</Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col lg={12} className="text-center py-5">
                  <p className="text-muted fs-5">{error ? 'Los proyectos estarán disponibles cuando se restablezca la conexión.' : selectedCategory === 'all' ? 'Todavía no hay proyectos publicados.' : 'No hay proyectos en esta categoría aún.'}</p>
                </Col>
              )}
            </Row>
          )}
        </Container>
      </section>
    </div>
  );
};

export default Gallery;

